<template>
   <div id="new-group-flight-request-wrapper">
      <div class="group-page-header">
         <h1 class="group-page-title">{{ $t("newGroupFlightRequest_title") }}</h1>
         <p class="group-page-subtitle">{{ $t("newGroupFlightRequest_subtitle") }}</p>
      </div>

      <div class="form-container">
         <!-- First Row: Group Name and Supplier -->
         <div class="row gy-3 mb-4">
            <div class="col-12 col-md-6">
               <div class="form-group">
                  <label class="form-label">{{ $t("newGroupFlightRequest_groupName") }}</label>
                  <div class="input-wrapper plain" :class="{ 'is-invalid': showValidationErrors && !formData.groupName.trim() }">
                     <input type="text" v-model="formData.groupName" :placeholder="$t('newGroupFlightRequest_groupNamePlaceholder')" class="form-input" />
                  </div>
               </div>
            </div>
            <div class="col-12 col-md-6" :class="{ 'select-invalid': showValidationErrors && !formData.groupType }">
               <UISelectBox
                  :label="$t('newGroupFlightRequest_groupType')"
                  id="groupType"
                  :options="groupTypeOptions"
                  v-model="formData.groupType"
                  :placeholder="$t('common_select')"
                  :no-auto-select="true" />
            </div>
         </div>

         <!-- Second Row: Origin and Destination Airports -->
         <div class="row gy-3 mb-4">
            <!-- Origin Airport -->
            <div class="col-12 col-md-6">
               <div class="form-group">
                  <label class="form-label">{{ $t("newGroupFlightRequest_departureAirport") }}</label>
                  <div class="input-wrapper plain" :class="{ 'is-active': isOriginDropdownOpen, 'is-invalid': showValidationErrors && !formData.originAirport }">
                     <input type="text" v-model="originSearchQuery" @input="handleOriginSearch" @focus="isOriginDropdownOpen = true" @blur="handleOriginBlur" class="form-input" />
                     <span v-if="isLoadingOrigin" class="g-radial-loader"></span>
                  </div>

                  <!-- Dropdown Results -->
                  <div v-if="isOriginDropdownOpen && originResults.length > 0" class="dropdown-results">
                     <div v-for="airport in originResults" :key="airport.id" @click="selectOrigin(airport)" class="dropdown-item">
                        <div class="airport-info">
                           <span class="airport-name">{{ airport.name }}</span>
                           <span class="airport-location">{{ airport.location }}</span>
                        </div>
                        <span class="airport-code">{{ airport.code }}</span>
                     </div>
                  </div>
               </div>
            </div>

            <!-- Destination Airport -->
            <div class="col-12 col-md-6">
               <div class="form-group">
                  <label class="form-label">{{ $t("newGroupFlightRequest_arrivalAirport") }}</label>
                  <div class="input-wrapper plain" :class="{ 'is-active': isDestinationDropdownOpen, 'is-invalid': showValidationErrors && !formData.destinationAirport }">
                     <input type="text" v-model="destinationSearchQuery" @input="handleDestinationSearch" @focus="isDestinationDropdownOpen = true" @blur="handleDestinationBlur" class="form-input" />
                     <span v-if="isLoadingDestination" class="g-radial-loader"></span>
                  </div>

                  <!-- Dropdown Results -->
                  <div v-if="isDestinationDropdownOpen && destinationResults.length > 0" class="dropdown-results">
                     <div v-for="airport in destinationResults" :key="airport.id" @click="selectDestination(airport)" class="dropdown-item">
                        <div class="airport-info">
                           <span class="airport-name">{{ airport.name }}</span>
                           <span class="airport-location">{{ airport.location }}</span>
                        </div>
                        <span class="airport-code">{{ airport.code }}</span>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- Third Row: Dates, Cabin Class, Passenger Count, Airline -->
         <div class="row gy-3 mb-4">
            <div class="col-12 col-md-3">
               <FlightDatePicker
                  :label="$t('newGroupFlightRequest_departureDate')"
                  v-model="formData.departureDate"
                  :sibling-date="formData.returnDate"
                  :allow-clear="false"
                  :hide-nav-buttons="true" />
            </div>
            <div class="col-12 col-md-3">
               <FlightDatePicker
                  :label="$t('newGroupFlightRequest_returnDate')"
                  v-model="formData.returnDate"
                  :sibling-date="formData.departureDate"
                  :is-return="true"
                  :allow-clear="true"
                  :hide-nav-buttons="true" />
            </div>
            <div class="col-12 col-md-2" :class="{ 'select-invalid': showValidationErrors && !formData.cabinClass }">
               <UISelectBox :label="$t('newGroupFlightRequest_cabinClass')" id="cabinClass" :options="cabinClassOptions" v-model="formData.cabinClass" :placeholder="$t('common_select')" />
            </div>
            <div class="col-12 col-md-2">
               <div class="form-group">
                  <label class="form-label">{{ $t("newGroupFlightRequest_passengerCount") }}</label>
                  <div class="paxes-wrapper" v-click-outside="closePaxesDropdown">
                     <div class="paxes-display" @click="isPaxesDropdownOpen = !isPaxesDropdownOpen">
                        <span>
                           A: <strong>{{ formData.paxCounts.ADULT }}</strong
                           >, C: <strong>{{ formData.paxCounts.CHILD }}</strong
                           >, I: <strong>{{ formData.paxCounts.INFANT }}</strong>
                        </span>
                     </div>
                     <div class="paxes-dropdown" v-if="isPaxesDropdownOpen">
                        <div class="pax-line">
                           <div class="pax-col">
                              <span>{{ $t("common_adult") }}</span>
                              <small>12+</small>
                           </div>
                           <div class="numerator-container">
                              <button type="button" class="numarator-btn" @click="decrementPax('ADULT')" :disabled="formData.paxCounts.ADULT <= 10">-</button>
                              <span>{{ formData.paxCounts.ADULT }}</span>
                              <button type="button" class="numarator-btn" @click="incrementPax('ADULT')" :disabled="totalPaxCount >= 45">+</button>
                           </div>
                        </div>
                        <div class="pax-line">
                           <div class="pax-col">
                              <span>{{ $t("common_child") }}</span>
                              <small>2-12</small>
                           </div>
                           <div class="numerator-container">
                              <button type="button" class="numarator-btn" @click="decrementPax('CHILD')" :disabled="formData.paxCounts.CHILD <= 0">-</button>
                              <span>{{ formData.paxCounts.CHILD }}</span>
                              <button type="button" class="numarator-btn" @click="incrementPax('CHILD')" :disabled="totalPaxCount >= 45">+</button>
                           </div>
                        </div>
                        <div class="pax-line">
                           <div class="pax-col">
                              <span>{{ $t("common_infant") }}</span>
                              <small>0-2</small>
                           </div>
                           <div class="numerator-container">
                              <button type="button" class="numarator-btn" @click="decrementPax('INFANT')" :disabled="formData.paxCounts.INFANT <= 0">-</button>
                              <span>{{ formData.paxCounts.INFANT }}</span>
                              <button type="button" class="numarator-btn" @click="incrementPax('INFANT')" :disabled="formData.paxCounts.INFANT >= formData.paxCounts.ADULT || totalPaxCount >= 45">
                                 +
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <div class="col-12 col-md-2" :class="{ 'select-invalid': showValidationErrors && !formData.airline }">
               <UISelectBox
                  :label="$t('newGroupFlightRequest_airline')"
                  id="airline"
                  :options="airlineOptions"
                  v-model="formData.airline"
                  :allow-search="false"
                  :placeholder="$t('common_select')"
                  :no-auto-select="true" />
            </div>
         </div>

         <!-- Fourth Row: Notes -->
         <div class="row gy-3 mb-4">
            <div class="col-12">
               <div class="form-group">
                  <label class="form-label">{{ $t("newGroupFlightRequest_notes") }}</label>
                  <textarea
                     v-model="formData.notes"
                     :placeholder="$t('newGroupFlightRequest_notesPlaceholder')"
                     rows="4"
                     :class="{ 'is-invalid': showValidationErrors && !formData.notes.trim() }"></textarea>
               </div>
            </div>
         </div>

         <!-- Submit Button Row -->
         <div class="row mb-4">
            <div class="col-12">
               <div class="submit-row">
                  <div class="info-text">
                     <span>{{ $t("newGroupFlightRequest_submitInfo") }}</span>
                  </div>
                  <Button :text="$t('newGroupFlightRequest_submit')" :class-name="'default'" @click.native="handleSubmit" />
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { addTask } from "@/service/tasklist.api.js";
import { generateUuid } from "@/utils.js";
import moment from "moment";
import Popup from "@/gts-popup.js";
import UISelectBox from "@/components/unitary/form2/UISelectBox.vue";
import Button from "@/components/unitary/Button.vue";
import FlightDatePicker from "@/components/structural/flight/search/FormElements/FlightDatePicker.vue";
import GtsDropdownData from "@/enums/_GtsDropdownData.js";
import { useAirportSearch } from "@/composables/useAirportSearch.js";
import { usePassengers } from "@/composables/usePassengers.js";
import { useDateUtils } from "@/composables/useDateUtils.js";

export default {
   name: "NewGroupFlightRequest",
   components: {
      UISelectBox,
      Button,
      FlightDatePicker,
   },
   setup() {
      const originSearch = useAirportSearch();
      const destinationSearch = useAirportSearch();
      const { calculateTotalPax } = usePassengers();
      const { formatToApiDateTime } = useDateUtils();

      return {
         originSearch,
         destinationSearch,
         calculateTotalPax,
         formatToApiDateTime,
      };
   },
   data() {
      return {
         formData: {
            groupName: "",
            groupType: null,
            originAirport: null,
            destinationAirport: null,
            airline: null,
            departureDate: moment().format(this.$constants.dateFormat),
            returnDate: "",
            cabinClass: null,
            paxCounts: {
               ADULT: 10,
               CHILD: 0,
               INFANT: 0,
            },
            notes: "",
         },
         groupTypeOptions: [],
         airlineOptions: [],
         cabinClassOptions: [],
         isPaxesDropdownOpen: false,
         showValidationErrors: false,
      };
   },
   computed: {
      totalPaxCount() {
         return this.calculateTotalPax(this.formData.paxCounts);
      },
      // Origin search computed properties
      originSearchQuery: {
         get() {
            return this.originSearch.searchQuery.value;
         },
         set(value) {
            this.originSearch.searchQuery.value = value;
         },
      },
      originResults() {
         return this.originSearch.results.value;
      },
      isOriginDropdownOpen: {
         get() {
            return this.originSearch.isDropdownOpen.value;
         },
         set(value) {
            this.originSearch.isDropdownOpen.value = value;
         },
      },
      isLoadingOrigin() {
         return this.originSearch.isLoading.value;
      },
      // Destination search computed properties
      destinationSearchQuery: {
         get() {
            return this.destinationSearch.searchQuery.value;
         },
         set(value) {
            this.destinationSearch.searchQuery.value = value;
         },
      },
      destinationResults() {
         return this.destinationSearch.results.value;
      },
      isDestinationDropdownOpen: {
         get() {
            return this.destinationSearch.isDropdownOpen.value;
         },
         set(value) {
            this.destinationSearch.isDropdownOpen.value = value;
         },
      },
      isLoadingDestination() {
         return this.destinationSearch.isLoading.value;
      },
   },
   watch: {
      "$store.state.app.airlineList": {
         handler: function (airlineList) {
            const allowedAirlines = ["TK", "XQ", "PC", "VF", "XC", "EW"];
            if (airlineList && airlineList.length > 0) {
               this.airlineOptions = airlineList
                  .filter((airline) => allowedAirlines.includes(airline.iataCode))
                  .map((airline) => ({
                     name: airline.nameTr || airline.nameEn || airline.iataCode,
                     value: airline.iataCode,
                  }));
            }
         },
         immediate: true,
      },
   },
   methods: {
      handleOriginSearch() {
         this.originSearch.handleSearch();
      },
      selectOrigin(airport) {
         this.originSearch.selectAirport(airport);
         this.formData.originAirport = airport;
      },
      handleOriginBlur() {
         this.originSearch.handleBlur();
      },
      handleDestinationSearch() {
         this.destinationSearch.handleSearch();
      },
      selectDestination(airport) {
         this.destinationSearch.selectAirport(airport);
         this.formData.destinationAirport = airport;
      },
      handleDestinationBlur() {
         this.destinationSearch.handleBlur();
      },
      incrementPax(type) {
         if (this.totalPaxCount < 45) {
            if (type === "INFANT" && this.formData.paxCounts.INFANT >= this.formData.paxCounts.ADULT) {
               return;
            }
            this.formData.paxCounts[type]++;
         }
      },
      decrementPax(type) {
         if (type === "ADULT" && this.formData.paxCounts.ADULT <= 10) {
            return;
         }
         if (this.formData.paxCounts[type] > 0) {
            this.formData.paxCounts[type]--;
         }
      },
      closePaxesDropdown() {
         this.isPaxesDropdownOpen = false;
      },
      async handleSubmit() {
         // Form validation
         const totalPax = this.formData.paxCounts.ADULT + this.formData.paxCounts.CHILD + this.formData.paxCounts.INFANT;
         const validations = {
            groupName: this.formData.groupName && this.formData.groupName.trim() !== "",
            groupType: !!this.formData.groupType,
            originAirport: !!this.formData.originAirport,
            destinationAirport: !!this.formData.destinationAirport,
            airline: !!this.formData.airline,
            departureDate: !!this.formData.departureDate,
            cabinClass: !!this.formData.cabinClass,
            passengerCount: totalPax >= 10 && totalPax <= 45,
            notes: this.formData.notes && this.formData.notes.trim() !== "",
         };

         const isValid = Object.values(validations).every((v) => v === true);

         if (!isValid) {
            this.showValidationErrors = true;
            Popup.warning(this.$t("common_warning"), this.$t("newGroupFlightRequest_fillAllRequiredFields"));
            return;
         }

         this.showValidationErrors = false;

         // Request model oluştur
         const requestModel = {
            type: 12, // PNR_GROUP_ADD
            orderId: 0,
            description: this.formData.notes || "",
            entityId: 0,
            errorId: 0,
            uuid: generateUuid(),
            groupPnrDetails: {
               paxCounts: this.formData.paxCounts,
               flexDayCount: 0,
               groupName: this.formData.groupName,
               groupType: this.formData.groupType,
               budgetPerPax: 0,
               currency: "EUR",
               cabinType: this.formData.cabinClass,
               departureAirport: this.formData.originAirport?.code,
               arrivalAirport: this.formData.destinationAirport?.code,
               departureAirlineCode: this.formData.airline,
               returnAirlineCode: this.formData.airline || null,
               departureDate: this.formatToApiDateTime(this.formData.departureDate),
               returnDate: this.formData.returnDate ? this.formatToApiDateTime(this.formData.returnDate) : null,
            },
         };

         this.$store.commit("app/setIsProgressGifActive", true);

         try {
            const response = await addTask(requestModel);

            if (response?.result?.success) {
               Popup.success(this.$t("common_success"), this.$t("newGroupFlightRequest_successMessage"), undefined, 10000, true).confirmed((res) => {
                  if (res.success) {
                     this.$router.push("/group/flight/tasks");
                  }
               });
            } else {
               const errorMessage = response?.result?.message || this.$t("common_error");
               Popup.error(this.$t("common_error"), errorMessage);
            }
         } catch (error) {
            Popup.error(this.$t("common_error"), this.$t("common_unexpectedError"));
         } finally {
            this.$store.commit("app/setIsProgressGifActive", false);
         }
      },
   },
   created() {
      this.cabinClassOptions = [
         { name: this.$t("enum_economy"), value: 1 },
         { name: this.$t("enum_business"), value: 2 },
      ];

      const dropdownData = new GtsDropdownData();
      this.groupTypeOptions = dropdownData.EFltPnrGroupType(false, false);
   },
};
</script>

<style lang="scss" scoped>
// UISelectBox border and background override
::v-deep .form-item.g-select-box-container {
   .g-select-box-wrapper .g-select-box {
      border: 1px solid var(--gray-400) !important;
      background: transparent !important;
   }
}

.select-invalid {
   ::v-deep .form-item.g-select-box-container {
      .g-select-box-wrapper .g-select-box {
         border-color: var(--red-500) !important;
         outline: 4px solid var(--red-100);
      }
   }
}

#new-group-flight-request-wrapper {
   .form-container {
      display: flex;
      flex-direction: column;
      gap: 0.95rem;
   }

   .form-group {
      position: relative;

      .form-label {
         display: block;
         margin-bottom: 0.5rem;
         font-weight: 500;
         color: var(--txt-color);
         font-size: var(--normal);
      }

      .input-wrapper {
         display: flex;
         align-items: center;
         gap: 0.5rem;
         height: 48px;
         border: 1.5px solid var(--gray-400);
         background-color: var(--gray-100);
         border-radius: 8px;
         padding: 0 0.75rem;
         transition: all 0.2s ease;

         &:hover {
            background-color: var(--surface-level-1);
         }

         &.is-active {
            border-color: var(--accent-primary);
            outline: 0.25rem solid var(--accent-primary-lighten);
         }

         &.is-invalid {
            border-color: var(--red-500) !important;
            outline: 4px solid var(--red-100);
         }

         &.plain {
            background-color: transparent;
            border: 1px solid var(--gray-400);

            &:hover {
               background-color: transparent;
            }
         }

         .gts-icon {
            flex-shrink: 0;
            color: var(--gray-500);
         }

         .form-input {
            flex: 1;
            border: none;
            background: transparent;
            outline: none;
            color: var(--txt-color);
            font-size: 1rem;

            &::placeholder {
               color: var(--gray-400);
            }
         }

         .g-radial-loader {
            flex-shrink: 0;
            border-color: var(--gray-500);
         }
      }

      .dropdown-results {
         position: absolute;
         top: calc(100% + 0.5rem);
         left: 0;
         right: 0;
         z-index: 1000;
         background: var(--surface-level-1);
         border: 2px solid var(--border-subtle);
         border-radius: 8px;
         box-shadow: var(--shadow-lg);
         max-height: 300px;
         overflow-y: auto;

         .dropdown-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 1rem;
            cursor: pointer;
            border-bottom: 1px solid var(--gray-200);
            transition: background-color 0.2s ease;

            &:last-child {
               border-bottom: none;
            }

            &:hover {
               background-color: var(--state-hover);
            }

            .airport-info {
               display: flex;
               flex-direction: column;
               gap: 0.25rem;
               flex: 1;

               .airport-name {
                  font-size: var(--normal);
                  color: var(--txt-color);
                  font-weight: 500;
               }

               .airport-location {
                  font-size: var(--small);
                  color: var(--gray-500);
               }
            }

            .airport-code {
               font-size: var(--normal);
               color: var(--gray-500);
               font-weight: 600;
               background: var(--surface-level-2);
               padding: 0.25rem 0.5rem;
               border-radius: 4px;
            }
         }
      }

      .passenger-counter-wrapper {
         display: flex;
         align-items: center;
         border: 1px solid var(--gray-400);
         border-radius: 8px;
         height: 48px;
         overflow: hidden;

         .counter-btn {
            flex: 0 0 auto;
            width: 40px;
            height: 100%;
            border: none;
            background: transparent;
            border-right: 1px solid var(--gray-400);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            color: var(--gray-700);
            transition: all 0.2s ease;

            &:last-child {
               border-right: none;
               border-left: 1px solid var(--gray-400);
            }

            &:hover:not(:disabled) {
               background: var(--state-hover);
            }

            &:disabled {
               opacity: 0.3;
               cursor: not-allowed;
            }

            span {
               line-height: 1;
            }
         }

         .counter-display {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: var(--large);
            font-weight: 400;
            color: var(--text-primary);
         }
      }

      .radio-group {
         display: flex;
         gap: 1.5rem;
         align-items: flex-end;
         height: 100%;
      }

      textarea {
         width: 100%;
         border: 1px solid var(--gray-400);
         background: transparent;
         border-radius: 8px;
         padding: 0.75rem;
         font-size: var(--normal);
         color: var(--txt-color);
         resize: vertical;
         font-family: inherit;

         &::placeholder {
            color: var(--gray-400);
         }

         &:focus {
            outline: none;
            border-color: var(--accent-primary);
         }

         &.is-invalid {
            border-color: var(--red-500) !important;
            outline: 4px solid var(--red-100);
         }
      }

      .paxes-wrapper {
         position: relative;

         .paxes-display {
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: 48px;
            border: 1px solid var(--gray-400);
            border-radius: 8px;
            padding: 0 0.75rem;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
               background-color: var(--surface-level-1);
            }

            span {
               font-size: var(--normal);
               color: var(--txt-color);

               strong {
                  font-weight: 600;
               }
            }

            i {
               color: var(--gray-500);
            }
         }

         .paxes-dropdown {
            position: absolute;
            top: calc(100% + 0.5rem);
            left: 0;
            right: 0;
            z-index: 1000;
            background: var(--surface-level-1);
            border: 2px solid var(--border-subtle);
            border-radius: 8px;
            box-shadow: var(--shadow-lg);
            padding: 1rem;
            min-width: 250px;

            .pax-line {
               display: flex;
               justify-content: space-between;
               align-items: center;
               padding: 0.5rem 0;

               .pax-col {
                  display: flex;
                  flex-direction: column;

                  span {
                     font-weight: 600;
                     font-size: var(--normal);
                     color: var(--txt-color);
                  }

                  small {
                     font-size: var(--small);
                     color: var(--gray-500);
                  }
               }

               .numerator-container {
                  display: flex;
                  align-items: center;
                  gap: 1rem;

                  span {
                     font-weight: 500;
                     font-size: 1.25rem;
                     min-width: 20px;
                     text-align: center;
                     color: var(--txt-color);
                  }

                  .numarator-btn {
                     outline: 0;
                     border: 0;
                     border-radius: 50%;
                     width: 28px;
                     height: 28px;
                     font-size: 1.25rem;
                     background-color: var(--gray-0);
                     color: var(--gray-700);
                     display: flex;
                     align-items: center;
                     justify-content: center;
                     box-shadow: var(--gts-box-shadow);
                     transition: all 0.15s ease;
                     cursor: pointer;

                     &:hover:not(:disabled) {
                        background-color: var(--accent-primary);
                        color: var(--gray-0);
                     }

                     &:disabled {
                        background-color: var(--gray-200);
                        opacity: 0.5;
                        cursor: not-allowed;
                     }
                  }
               }
            }
         }
      }
   }

   .submit-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      // padding: 1.5rem 0;

      .info-text {
         display: flex;
         align-items: center;
         gap: 0.5rem;
         color: var(--gray-500);
         font-size: var(--small);

         i {
            color: var(--accent-primary);
         }
      }
   }

   // FlightDatePicker styling
   ::v-deep .g-input-container {
      .g-input-text-wrapper {
         input {
            border: 1px solid var(--gray-400) !important;
            background: transparent !important;
            border-radius: 8px !important;
            height: 48px !important;
         }
      }

      label {
         font-size: var(--normal);
         font-weight: 500;
         margin-bottom: 0.5rem;
         color: var(--text-primary);
      }
   }
}
</style>
